import { NotificationListResponseType } from "@sellar-io/api-v2/types";
import { twMerge } from "tailwind-merge";
import {
  DIRECT_MESSAGE,
  ORDER_DETAILS,
  ORDER_BUYER,
  NOTIFICATIONS,
  ORDER_COMMENT,
  SELLAR_CREDIT_TAB,
} from "../../constants/routes";
import { useNotificationReadCreate } from "../../hooks/api/v2/notificationRead/useNotificationReadCreate";
import { NotificationGroupContent } from "./NotificationGroupContent";
import { Icon } from "@sellar-io/components";
import { useNavigateToChild } from "../../hooks/useParentNavigation";

const getSellarCreditNavigationUrl = (
  notification: NotificationListResponseType[number],
) => {
  switch (notification.subtype) {
    case "sellar_credit_low_balance":
      return SELLAR_CREDIT_TAB;
    case "sellar_credit_offer_expiry":
      return `${ORDER_DETAILS.href({ orderId: notification.entityId })}?tab=All+orders`;
    default:
      return NOTIFICATIONS;
  }
};

const getOrderNavigationUrl = (
  notification: NotificationListResponseType[number],
) => {
  switch (notification.subtype) {
    case "order_comment_added":
      return ORDER_COMMENT.href({ orderId: notification.entityId });
    default:
      return `${ORDER_DETAILS.href({ orderId: notification.entityId })}?tab=All+orders`;
  }
};

const getNavigateUrl = (notification: NotificationListResponseType[number]) => {
  switch (notification.type) {
    case "message":
      return DIRECT_MESSAGE.href({ relationshipId: notification.entityId });
    case "order":
      return getOrderNavigationUrl(notification);
    case "storefront":
      return `${ORDER_BUYER.href({ supplierIdOrUrlId: notification.SenderBusiness.urlId ?? "" })}`;
    case "sellar_credit":
      return getSellarCreditNavigationUrl(notification);
    default:
      return NOTIFICATIONS;
  }
};

export const NotificationGroup = ({
  notification,
  setPopoverActive,
  isNotificationPopover,
}: {
  notification: NotificationListResponseType[number];
  setPopoverActive?: (active: boolean) => void;
  isNotificationPopover: boolean;
}) => {
  const navigateToChild = useNavigateToChild();
  const mutation = useNotificationReadCreate();
  const navigateUrl = getNavigateUrl(notification);

  const markAsRead = () => {
    if (!!notification.NotificationRead) {
      if (navigateUrl) {
        navigateToChild(navigateUrl);
      }
      return setPopoverActive?.(false);
    }
    mutation.mutate(
      {
        NotificationId: notification.id,
      },
      {
        onSuccess: () => {
          if (navigateUrl) {
            navigateToChild(navigateUrl);
          }
          setPopoverActive?.(false);
        },
      },
    );
  };

  const SenderBusiness = notification.SenderBusiness;
  const logoUrl = SenderBusiness.logoUrl;
  const senderBusinessName = SenderBusiness.name;
  const title = notification.title;
  const description = notification.description;
  const isNotificationRead = !!notification.NotificationRead;
  return (
    <NotificationGroupContent
      isNotificationRead={isNotificationRead}
      description={description}
      title={title}
      senderBusinessName={senderBusinessName}
      logoUrl={logoUrl}
      markAsRead={markAsRead}
      isNotificationPopover={isNotificationPopover}
    >
      {notification.type !== "storefront" && (
        <NotificationIcon notification={notification} />
      )}
    </NotificationGroupContent>
  );
};

const NotificationIcon = ({
  notification,
}: {
  notification: NotificationListResponseType[number];
}) => {
  // order_requested
  // order_comment_added
  // order_paid
  // storefront_availability_alert
  // sellar_credit_low_balance
  const getIconClasses = (): {
    bg: string;
    icon: React.ReactNode;
  } => {
    switch (notification.subtype) {
      case "order_delivered":
        return {
          bg: "bg-blue-100",
          icon: <Icon icon={"CHECK_ALL"} className={"text-blue-700"} />,
        };
      case "order_dispatched":
        return {
          bg: "bg-purple-100",
          icon: (
            <Icon
              icon={"PACKAGE_VARIANT_CLOSED"}
              className={"text-purple-700"}
            />
          ),
        };
      case "order_cancelled":
        return {
          bg: "bg-red-100",
          icon: <Icon icon={"CLOSE"} className={"text-red-700"} />,
        };
      case "order_accepted":
        return {
          bg: "bg-orange-100",
          icon: (
            <Icon icon={"THUMB_UP_OUTLINE"} className={"text-orange-700"} />
          ),
        };
      case "message_received":
        return {
          bg: "bg-white",
          icon: <Icon icon={"MESSAGE_OUTLINE"} className={"text-gray-800"} />,
        };
      case "order_comment_added":
        return {
          bg: "bg-white",
          icon: (
            <Icon icon={"COMMENT_TEXT_OUTLINE"} className={"text-gray-800"} />
          ),
        };
      case "order_sellar_credit_added_tab":
        return {
          bg: "bg-gradient-to-br from-[#B557F1] to-[#D213A6]",
          icon: <Icon icon={"SELLAR_NEW"} className={"text-white"} />,
        };
      case "order_delivery_date_changed":
        return {
          bg: "bg-white",
          icon: <Icon icon={"TRUCK_OUTLINE"} className={"text-gray-800"} />,
        };
      case "sellar_credit_sole_trader_declined":
      case "sellar_credit_low_balance":
        return {
          bg: "bg-gradient-to-br from-[#B557F1] to-[#D213A6]",
          icon: <Icon icon={"SELLAR_NEW"} className={"text-white"} />,
        };

      default:
        return {
          bg: "bg-yellow-100",
          icon: (
            <Icon icon={"TIMER_SAND_EMPTY"} className={"text-yellow-800"} />
          ),
        };
    }
  };

  const { bg, icon } = getIconClasses();

  return (
    <>
      <div
        className={twMerge(
          "absolute top-[26px] left-[25px] inline-flex h-6 w-6 items-center justify-center rounded-3xl p-1 shadow-[0px_2px_2px_0px_rgba(0,0,0,0.15)]",
          bg,
        )}
      >
        {icon}
      </div>
    </>
  );
};
